// Centralized mapping between the backend's `ReservationTag` enum
// (stored on `Reservation.tags`) and the Spanish labels shown in the UI.

import type { ApiReservationTag } from "@/types/api";

export interface ReservationTagOption {
  value: ApiReservationTag;
  label: string;
}

// Same order as `ReservationTag` in backend/apps/reservations/enums.py —
// that order is what the picker renders.
export const RESERVATION_TAGS: readonly ReservationTagOption[] = [
  { value: "gluten_free", label: "Sin gluten" },
  { value: "vegetarian", label: "Vegetariano" },
  { value: "vegan", label: "Vegano" },
  { value: "dairy_free", label: "Sin lactosa" },
  { value: "nut_allergy", label: "Alergia frutos secos" },
  { value: "shellfish_allergy", label: "Alergia mariscos" },
  { value: "diabetic", label: "Diabético" },
  // El prefijo "Con " los mantiene distinguibles de las TableFeature
  // "Mascota" / "Silla de bebé", que se renderizan en el mismo panel de
  // detalle pero son una restricción de mesa, no del comensal.
  { value: "with_infant", label: "Con bebé" },
  { value: "with_pet", label: "Con mascota" },
] as const;

// Mirrors `ReservationTag.dietary()` on the backend: lo que el comensal
// puede o no comer. Llegar con perro o con bebé no es una restricción de
// cocina, y los reportes titulan su tarjeta "Restricciones" — sin esta
// separación le dirían al chef que hay dos dietas que preparar.
export const DIETARY_TAGS: readonly ReservationTagOption[] = RESERVATION_TAGS.filter(
  (t) => t.value !== "with_infant" && t.value !== "with_pet",
);

// El resto: contexto de quién se sienta a la mesa.
export const PARTY_CONTEXT_TAGS: readonly ReservationTagOption[] = RESERVATION_TAGS.filter(
  (t) => t.value === "with_infant" || t.value === "with_pet",
);

export const DIETARY_TAG_VALUES: ReadonlySet<string> = new Set(DIETARY_TAGS.map((t) => t.value));

const LABEL_BY_VALUE: Record<ApiReservationTag, string> = RESERVATION_TAGS.reduce(
  (acc, { value, label }) => {
    acc[value] = label;
    return acc;
  },
  {} as Record<ApiReservationTag, string>,
);

export function reservationTagLabel(value: string): string {
  return LABEL_BY_VALUE[value as ApiReservationTag] ?? value;
}

export function isDietaryTag(value: string): boolean {
  return DIETARY_TAG_VALUES.has(value);
}
